import React, { useState, useEffect } from "react";
import ExampleTheme from "./themes/ExampleTheme";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import ToolbarPlugin from "./plugins/ToolbarPlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import { ListItemNode, ListNode } from "@lexical/list";
import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { TRANSFORMERS, $convertFromMarkdownString } from "@lexical/markdown";

import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
} from "lexical";
import {
  $createTableNode,
  $createTableRowNode, 
  $createTableCellNode
} from "@lexical/table";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import "./styles.css";
import EmojisPlugin from "./plugins/EmojisPlugin";
import EmojiPickerPlugin from "./plugins/EmojiPickerPlugin";
import { EmojiNode } from "./node/EmojiNode";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";

function Placeholder() {
  return <div className="editor-placeholder"></div>;
}

const ClearTextPlugin = ({ sent }) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      if (root && sent) {
        root.clear();
      }
    });
  }, [editor, sent]);

  return null;
};

// Plugin to handle initial content after editor is ready
const InitialContentPlugin = ({ initialText }) => {
  const [editor] = useLexicalComposerContext();
  const [initialized, setInitialized] = useState(false);

  const processInlineMarkdown = (text) => {
    const nodes = [];
    let remaining = text;
    
    while (remaining.length > 0) {
      const boldPos = remaining.indexOf('**');
      const codePos = remaining.indexOf('`');
      
      let type = '';
      let pattern = null;
      
      if (boldPos >= 0 && (codePos < 0 || boldPos < codePos)) {
        const boldMatch = remaining.match(/^(.*?)\*\*([^*]+?)\*\*(.*)/s);
        if (boldMatch) {
          type = 'bold';
          pattern = boldMatch;
        }
      } else if (codePos >= 0) {
        const codeMatch = remaining.match(/^(.*?)`([^`]+?)`(.*)/s);
        if (codeMatch) {
          type = 'code';
          pattern = codeMatch;
        }
      }
      
      if (pattern) {
        const [, before, content, after] = pattern;
        
        if (before) {
          nodes.push($createTextNode(before));
        }
        
        const formattedNode = $createTextNode(content);
        formattedNode.setFormat(type);
        nodes.push(formattedNode);
        
        remaining = after;
      } else {
        nodes.push($createTextNode(remaining));
        break;
      }
    }
    
    return nodes;
  };

  const processMarkdownContent = (markdownText) => {
    const lines = markdownText.split('\n');
    const nodes = [];
    let inTable = false;
    let tableLines = [];

    for (const line of lines) {
      if (line.includes('|') && line.trim() !== '') {
        if (!inTable) {
          inTable = true;
          tableLines = [];
        }
        tableLines.push(line);
      } else {
        if (inTable) {
          const tableNode = createLexicalTableFromLines(tableLines);
          if (tableNode) {
            nodes.push(tableNode);
          }
          inTable = false;
          tableLines = [];
        }
        
        if (line.trim()) {
          const processedNode = processRegularLine(line);
          if (processedNode) {
            nodes.push(processedNode);
          }
        }
      }
    }

    if (inTable && tableLines.length > 0) {
      const tableNode = createLexicalTableFromLines(tableLines);
      if (tableNode) {
        nodes.push(tableNode);
      }
    }

    return nodes;
  };

  const createLexicalTableFromLines = (tableLines) => {
    if (tableLines.length < 2) return null;
    
    const tableRows = [];
    let headerProcessed = false;

    for (const line of tableLines) {
      const trimmedLine = line.trim();
      
      if (/^[\|\-\s:]+$/.test(trimmedLine)) continue;

      const cells = trimmedLine.split('|')
        .map(cell => cell.trim())
        .filter((cell, index, array) => 
          !(cell === '' && (index === 0 || index === array.length - 1))
        );

      if (cells.length === 0) continue;

      const rowCells = cells.map(cellText => {
        const cell = $createTableCellNode(headerProcessed ? 0 : 1);
        const paragraph = $createParagraphNode();
        const textNodes = processInlineMarkdown(cellText);
        textNodes.forEach(node => paragraph.append(node));
        cell.append(paragraph);
        return cell;
      });

      const row = $createTableRowNode();
      rowCells.forEach(cell => row.append(cell));
      tableRows.push(row);
      
      headerProcessed = true;
    }

    if (tableRows.length === 0) return null;

    const table = $createTableNode();
    tableRows.forEach(row => table.append(row));
    return table;
  };

  const processRegularLine = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return null;

    const paragraph = $createParagraphNode();
    let content = trimmed;
    
    if (trimmed.startsWith('### ')) {
      content = trimmed.substring(4);
    } else if (trimmed.startsWith('## ')) {
      content = trimmed.substring(3);
    } else if (trimmed.startsWith('# ')) {
      content = trimmed.substring(2);
    }
    
    const textNodes = processInlineMarkdown(content);
    textNodes.forEach(node => paragraph.append(node));
    return paragraph;
  };

  useEffect(() => {
    if (!initialized && initialText && initialText.trim()) {
      editor.update(() => {
        const root = $getRoot();
        const currentChildren = root.getChildren();
        
        if (currentChildren.length === 0 || 
            (currentChildren.length === 1 && currentChildren[0].getTextContent().trim() === "")) {
          
          root.clear();
          
          try {
            const nodes = processMarkdownContent(initialText);
            
            if (nodes && nodes.length > 0) {
              nodes.forEach(node => {
                if (node) {
                  root.append(node);
                }
              });
              return;
            }
            
            try {
              $convertFromMarkdownString(initialText, TRANSFORMERS);
              
              if (root.getChildren().length === 0) {
                const p = $createParagraphNode();
                p.append($createTextNode(initialText));
                root.append(p);
              }
            } catch (lexicalError) {
              const p = $createParagraphNode();
              p.append($createTextNode(initialText));
              root.append(p);
            }
            
          } catch (error) {
            const p = $createParagraphNode();
            p.append($createTextNode(initialText));
            root.append(p);
          }
        }
      });
      
      setInitialized(true);
    }
  }, [editor, initialText, initialized]);

  return null;
};

const Editor = ({
  onSelectionChange,
  initialText = "",
  onChange,
  sent,
  includeClearTextPlugin = false,
}) => {
  const editorConfig = {
    theme: ExampleTheme,
    onError(error) {
      throw error;
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      EmojiNode,
    ],
    editorState: null,
  };

  const createHTMLSerializer = (value) => ({
    toHTML: () => {
      let html = '';
      value.read(() => {
        const rootElement = document.createElement('div');
        const root = $getRoot();
        
        rootElement.style.fontFamily = 'Arial, sans-serif';
        rootElement.style.fontSize = '12pt';
        rootElement.style.lineHeight = '1.5';
        
        const formatTextNode = (textNode, element) => {
          const format = textNode.getFormat();
          const text = textNode.getTextContent();
          
          if (!text) return;
          
          const createElement = (tag) => {
            const el = document.createElement(tag);
            el.textContent = text;
            return el;
          };
          
          if (format & 1) element.appendChild(createElement('strong'));
          else if (format & 2) element.appendChild(createElement('em'));
          else if (format & 8) element.appendChild(createElement('u'));
          else if (format & 16) element.appendChild(createElement('s'));
          else if (format & 32) element.appendChild(createElement('code'));
          else element.appendChild(document.createTextNode(text));
        };
        
        root.getChildren().forEach(node => {
          const nodeType = node.getType();
          let element;
          
          if (nodeType === 'heading') {
            element = document.createElement(node.__tag || 'h1');
          } else if (nodeType === 'list') {
            element = document.createElement(node.getTag());
            node.getChildren().forEach(listItemNode => {
              const li = document.createElement('li');
              listItemNode.getChildren().forEach(child => {
                if (child.getType() === 'text') {
                  formatTextNode(child, li);
                }
              });
              element.appendChild(li);
            });
            rootElement.appendChild(element);
            return;
          } else {
            element = document.createElement('p');
          }
          
          node.getChildren().forEach(child => {
            if (child.getType() === 'text') {
              formatTextNode(child, element);
            }
          });
          
          rootElement.appendChild(element);
        });
        
        html = rootElement.innerHTML;
      });
      return html;
    },
    toText: () => {
      let text = '';
      value.read(() => {
        text = $getRoot().getTextContent();
      });
      return text;
    }
  });

  const handleContentChange = (value) => {
    const editorStateJSON = value.toJSON();
    const enhancedEditorState = {
      ...editorStateJSON,
      ...createHTMLSerializer(value)
    };
    
    if (onChange) {
      onChange(enhancedEditorState);
    }
  };

  const handleSelectionChange = (selection) => {
    if (onSelectionChange) {
      onSelectionChange(selection);
    }
  };

  return (
    <LexicalComposer initialConfig={editorConfig}>
      <div className="editor-container">
        <ToolbarPlugin />
        <div className="editor-inner">
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="editor-input" />
            }
            onSelectionChange={handleSelectionChange}
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
            onChange={handleContentChange}
          />
          <OnChangePlugin onChange={handleContentChange} />
          <InitialContentPlugin initialText={initialText} />
          <EmojisPlugin />
          <EmojiPickerPlugin />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <CodeHighlightPlugin />
          <ListPlugin />
          <LinkPlugin />
          <TablePlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          {includeClearTextPlugin && <ClearTextPlugin sent={sent} />}
        </div>
      </div>
    </LexicalComposer>
  );
};

export default Editor;
